import React, { useState, useEffect, useMemo } from "react";
import { 
  HiPencil, HiTrash, HiCheckCircle, HiPlus, HiSearch, 
  HiOutlineUserGroup, HiOutlinePencilAlt, HiOutlineTrash, 
  HiOutlinePlusCircle, HiOutlineUserCircle, HiOutlineCode, 
  HiBan, HiShieldExclamation, HiClock, HiOutlineMoon, 
  HiOutlineEye, HiOutlineEyeOff, HiOutlineUserAdd,
  HiMail, HiPhone, HiCalendar, HiUser, HiIdentification,
  HiFilter, HiRefresh, HiChevronDown, HiChevronUp, 
  HiDotsVertical, HiX
} from "react-icons/hi";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import * as bootstrap from "bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { PulseLoader } from "react-spinners";

// Configuration Constants
const accessOptions = [
  { value: "Edit", label: "Edit", icon: <HiOutlinePencilAlt className="me-2" /> },
  { value: "Delete", label: "Delete", icon: <HiOutlineTrash className="me-2" /> },
  { value: "Add", label: "Add", icon: <HiOutlinePlusCircle className="me-2" /> }
];

const roleOptions = [
  { value: "Designer", label: "Designer", color: "#fd7e14" },
  { value: "Developer", label: "Developer", color: "#20c997" }
];

const statusOptions = [
  { value: "Available", label: "Available", color: "success" },
  { value: "Busy", label: "Busy", color: "danger" },
  { value: "Do Not Disturb", label: "Do Not Disturb", color: "dark" },
  { value: "Be Right Back", label: "Be Right Back", color: "warning" },
  { value: "Away", label: "Away", color: "secondary" },
  { value: "Offline", label: "Offline", color: "dark" }
];

export default function DeveloperTable() {
  // State Management
  const [developers, setDevelopers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [rolemanagement, setRoleManagement] = useState("");
  
  // Filter & Pagination State
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [developersPerPage] = useState(8);

  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [developerId, setDeveloperId] = useState(null);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", dob: "", 
    role: "Developer", status: "Available", photo: null
  });
  const [selectedAccess, setSelectedAccess] = useState([]);
  const [developerToDelete, setDeveloperToDelete] = useState(null);

  // Responsive State
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    fetchDevelopers();
    
    // Extract role from token
    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setRoleManagement(payload.role);
      } catch (e) { console.error("Token error", e); }
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDevelopers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        "http://localhost:5890/adminperson/developers",
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );
      setDevelopers(response.data);
    } catch (err) {
      toast.error("Failed to load developers");
    } finally {
      setIsLoading(false);
    }
  };

  // Logic: Filtering
  const filteredData = useMemo(() => {
    return developers.filter(dev => {
      const matchesSearch = dev.developerName?.toLowerCase().includes(search.toLowerCase()) || 
                           dev.developerEmail?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = filterStatus === "all" || dev.developerStatus === filterStatus;
      const matchesRole = filterRole === "all" || dev.developerrole === filterRole;
      return matchesSearch && matchesStatus && matchesRole;
    });
  }, [developers, search, filterStatus, filterRole]);

  // Pagination
  const indexOfLast = currentPage * developersPerPage;
  const currentItems = filteredData.slice(indexOfLast - developersPerPage, indexOfLast);
  const totalPages = Math.ceil(filteredData.length / developersPerPage);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", phone: "", dob: "", role: "Developer", status: "Available", photo: null });
    setSelectedAccess([]);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const submitData = new FormData();
    submitData.append("developerName", formData.name);
    submitData.append("developerEmail", formData.email);
    submitData.append("developerMobileNumber", formData.phone);
    submitData.append("dateOfBirth", formData.dob);
    submitData.append("developerStatus", formData.status);
    submitData.append("developerrole", formData.role);
    submitData.append("accessPermissions", JSON.stringify(selectedAccess.map(a => a.value)));
    if (formData.photo) submitData.append("developerImageFile", formData.photo);

    try {
      const url = isEditing 
        ? `http://localhost:5890/adminperson/developers/${developerId}`
        : "http://localhost:5890/developers";
      
      const method = isEditing ? 'put' : 'post';
      await axios[method](url, submitData, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
      });

      toast.success(isEditing ? "Updated Successfully" : "Developer Added Successfully");
      fetchDevelopers();
      bootstrap.Modal.getInstance(document.getElementById('devModal')).hide();
    } catch (err) {
      toast.error(err.response?.data?.error || "Action failed");
    }
  };

  // Helper: Status Badges
  const getStatusConfig = (status) => {
    const config = {
      "Available": { color: "success", icon: <HiCheckCircle /> },
      "Busy": { color: "danger", icon: <HiBan /> },
      "Away": { color: "warning text-dark", icon: <HiOutlineMoon /> },
      "Offline": { color: "secondary", icon: <HiOutlineEyeOff /> },
    };
    return config[status] || { color: "primary", icon: <HiCheckCircle /> };
  };

  return (
    <div className="container-fluid py-4 bg-light min-vh-100">
      <ToastContainer />
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="card-header bg-white border-0 p-4">
          <div className="row g-3 align-items-center">
            <div className="col-12 col-lg-4">
              <h4 className="fw-bold mb-0">Team Management</h4>
              <p className="text-muted small mb-0">View and manage your technical workforce</p>
            </div>
            
            <div className="col-12 col-md-8 col-lg-5">
              <div className="input-group bg-light rounded-pill px-3 py-1">
                <span className="input-group-text bg-transparent border-0 text-muted"><HiSearch /></span>
                <input 
                  type="text" 
                  className="form-control bg-transparent border-0 shadow-none" 
                  placeholder="Search by name or email..." 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-12 col-md-4 col-lg-3 text-md-end">
              <div className="d-flex gap-2 justify-content-md-end">
                <button className="btn btn-light rounded-pill border shadow-sm" onClick={() => setShowFilters(!showFilters)}>
                  <HiFilter />
                </button>
                {["Admin", "Manager"].includes(rolemanagement) && (
                  <button 
                    className="btn btn-primary rounded-pill px-4 shadow-sm d-flex align-items-center gap-2"
                    data-bs-toggle="modal" data-bs-target="#devModal" onClick={resetForm}
                  >
                    <HiPlus /> <span className="d-none d-sm-inline">Add Member</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="row g-3 mt-3 p-3 bg-light rounded-4 animate__animated animate__fadeIn">
              <div className="col-md-5">
                <label className="small fw-bold mb-1">Status Filter</label>
                <Select 
                  options={[{value: "all", label: "All Status"}, ...statusOptions]}
                  onChange={(s) => setFilterStatus(s.value)}
                  className="rounded-3"
                />
              </div>
              <div className="col-md-5">
                <label className="small fw-bold mb-1">Role Filter</label>
                <Select 
                  options={[{value: "all", label: "All Roles"}, ...roleOptions]}
                  onChange={(r) => setFilterRole(r.value)}
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button className="btn btn-outline-secondary w-100 rounded-3" onClick={() => {setFilterStatus("all"); setFilterRole("all");}}>
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
    <div className="row g-3 mb-4">
        {[
          { label: "Total Developers", count: developers.length, color: "primary", icon: <HiOutlineUserGroup /> },
          { label: "On Duty", count: developers.filter(d => d.developerStatus === "Available").length, color: "success", icon: <HiCheckCircle /> },
          { label: "Busy", count: developers.filter(d => d.developerStatus === "Busy").length, color: "danger", icon: <HiBan /> },
          { label: "Developers", count: developers.filter(d => d.developerrole === "Developer").length, color: "info", icon: <HiOutlineCode /> },
        ].map((stat, i) => (
          <div key={i} className="col-12 col-sm-6 col-xl-3">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body d-flex align-items-center p-3">
                <div className={`rounded-circle bg-${stat.color} bg-opacity-10 p-3 me-3 text-${stat.color} fs-3`}>
                  {stat.icon}
                </div>
                <div>
                  <h6 className="text-muted mb-1 small text-uppercase fw-bold">{stat.label}</h6>
                  <h4 className="mb-0 fw-bold">{stat.count}</h4>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

        {/* --- DESKTOP TABLE --- */}
        {!isMobile ? (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light text-muted small text-uppercase">
                <tr>
                  <th className="px-4 py-3 border-0">Developer</th>
                  <th className="py-3 border-0">Role</th>
                  <th className="py-3 border-0">Contact</th>
                  <th className="py-3 border-0">Status</th>
                  <th className="px-4 py-3 border-0 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="5" className="text-center py-5"><PulseLoader color="#0d6efd" /></td></tr>
                ) : currentItems.map(dev => (
                  <tr key={dev.id} className="border-bottom">
                    <td className="px-4 py-3">
                      <div className="d-flex align-items-center">
                        <img 
                          src={dev.developerImageFile ? `http://localhost:5890/uploads/${dev.developerImageFile}` : "https://ui-avatars.com/api/?name="+dev.developerName} 
                          className="rounded-circle me-3 border" width="45" height="45" alt=""
                        />
                        <div>
                          <div className="fw-bold">{dev.developerName}</div>
                          <div className="text-muted extra-small">ID: {dev.developerid || 'N/A'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge rounded-pill bg-light text-dark border px-3 py-2 fw-medium">
                        {dev.developerrole === 'Designer' ? <HiOutlinePencilAlt className="me-1" /> : <HiOutlineCode className="me-1" />}
                        {dev.developerrole || "Developer"}
                      </span>
                    </td>
                    <td>
                      <div className="small fw-medium">{dev.developerEmail}</div>
                      <div className="text-muted small">{dev.developerMobileNumber}</div>
                    </td>
                    <td>
                      <span className={`badge bg-${getStatusConfig(dev.developerStatus).color} bg-opacity-10 text-${getStatusConfig(dev.developerStatus).color} px-3 py-2 rounded-pill`}>
                        {getStatusConfig(dev.developerStatus).icon} <span className="ms-1">{dev.developerStatus}</span>
                      </span>
                    </td>
                    <td className="px-4 text-end">
                      <div className="btn-group shadow-sm rounded-3">
                        <button className="btn btn-white btn-sm px-3" onClick={() => {
                          setDeveloperId(dev.id);
                          setFormData({ name: dev.developerName, email: dev.developerEmail, phone: dev.developerMobileNumber, dob: dev.dateOfBirth, role: dev.developerrole, status: dev.developerStatus });
                          setIsEditing(true);
                          new bootstrap.Modal(document.getElementById('devModal')).show();
                        }}><HiPencil className="text-primary" /></button>
                        <button className="btn btn-white btn-sm px-3" onClick={() => {
                          setDeveloperToDelete(dev);
                          new bootstrap.Modal(document.getElementById('deleteModal')).show();
                        }}><HiTrash className="text-danger" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* --- MOBILE CARD VIEW --- */
          <div className="p-3">
            {currentItems.map(dev => (
              <div key={dev.id} className="card border rounded-4 mb-3 shadow-sm overflow-hidden">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      <img src={`https://ui-avatars.com/api/?name=${dev.developerName}`} className="rounded-circle me-2" width="40" alt="" />
                      <div>
                        <h6 className="mb-0 fw-bold">{dev.developerName}</h6>
                        <span className="text-muted small">{dev.developerrole}</span>
                      </div>
                    </div>
                    <span className={`badge h-fit bg-${getStatusConfig(dev.developerStatus).color} py-2`}>
                      {dev.developerStatus}
                    </span>
                  </div>
                  <div className="mb-3">
                    <div className="small text-muted"><HiMail className="me-1" /> {dev.developerEmail}</div>
                    <div className="small text-muted"><HiPhone className="me-1" /> {dev.developerMobileNumber}</div>
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-primary btn-sm flex-grow-1 rounded-3" onClick={() => {/* logic */}}>Edit</button>
                    <button className="btn btn-outline-danger btn-sm px-3 rounded-3"><HiTrash /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* --- PAGINATION --- */}
        <div className="card-footer bg-white border-0 p-4 d-flex justify-content-between align-items-center">
          <span className="text-muted small">Showing {currentItems.length} of {filteredData.length} entries</span>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link rounded-circle mx-1 border-0 shadow-sm" onClick={() => setCurrentPage(prev => prev - 1)}>‹</button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                  <button className="page-link rounded-circle mx-1 border-0 shadow-sm" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link rounded-circle mx-1 border-0 shadow-sm" onClick={() => setCurrentPage(prev => prev + 1)}>›</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* --- ADD/EDIT MODAL --- */}
      <div className="modal fade" id="devModal" tabIndex="-1" style={{marginTop:"60px"}}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow rounded-4">
            <div className="modal-header border-0 pb-0">
              <h5 className="fw-bold">{isEditing ? `Edit Developer ${formData.name}` : "Add New Developer"}</h5>
              <button type="button" className="btn-close shadow-none" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Full Name</label>
                  <input type="text" name="name" className="form-control rounded-3" value={formData.name} onChange={handleInputChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Email Address</label>
                  <input type="email" name="email" className="form-control rounded-3" value={formData.email} onChange={handleInputChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Mobile Number</label>
                  <input type="text" name="phone" className="form-control rounded-3" value={formData.phone} onChange={handleInputChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Date of Birth</label>
                  <input type="date" name="dob" className="form-control rounded-3" value={formData.dob} onChange={handleInputChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Role Type</label>
                  <select name="role" className="form-select rounded-3" value={formData.role} onChange={handleInputChange}>
                    <option value="Developer">Developer</option>
                    <option value="Designer">Designer</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Initial Status</label>
                  <select name="status" className="form-select rounded-3" value={formData.status} onChange={handleInputChange}>
                    {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Permissions</label>
                  <Select 
                    isMulti 
                    options={accessOptions} 
                    value={selectedAccess} 
                    onChange={setSelectedAccess}
                    className="rounded-3"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Profile Picture</label>
                  <input type="file" className="form-control rounded-3" onChange={(e) => setFormData(p => ({...p, photo: e.target.files[0]}))} />
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button className="btn btn-light rounded-pill px-4" data-bs-dismiss="modal">Cancel</button>
              <button className="btn btn-primary rounded-pill px-4" onClick={handleSave}>
                {isEditing ? "Update Profile" : "Create Account"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- DELETE CONFIRMATION MODAL --- */}
      <div className="modal fade" id="deleteModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content border-0 shadow rounded-4 text-center">
            <div className="modal-body p-4">
              <div className="bg-danger bg-opacity-10 text-danger rounded-circle p-3 d-inline-block mb-3">
                <HiOutlineTrash size={40} />
              </div>
              <h5 className="fw-bold">Are you sure?</h5>
              <p className="text-muted small">This action cannot be undone. This will permanently delete the developer account.</p>
              <div className="d-grid gap-2">
                <button className="btn btn-danger rounded-pill" onClick={async () => {
                  try {
                    await axios.delete(`http://localhost:5890/adminperson/developers/${developerToDelete.id}`, {
                      headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
                    });
                    toast.success("Deleted successfully");
                    fetchDevelopers();
                    bootstrap.Modal.getInstance(document.getElementById('deleteModal')).hide();
                  } catch (e) { toast.error("Delete failed"); }
                }}>Confirm Delete</button>
                <button className="btn btn-light rounded-pill" data-bs-dismiss="modal">Keep Member</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .extra-small { font-size: 10px; }
        .h-fit { height: fit-content; }
        .page-link { color: #6c757d; transition: all 0.2s; }
        .page-item.active .page-link { background-color: #0d6efd; border-color: #0d6efd; color: white; transform: scale(1.1); }
        .btn-white { background: #fff; border: 1px solid #eee; }
        .btn-white:hover { background: #f8f9fa; }
        .card { transition: transform 0.2s; }
        @media (max-width: 576px) {
          .container-fluid { padding: 1rem !important; }
        }
      `}</style>
    </div>
  );
}